import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { getRoot } from 'mobx-easy';

import {
	customAttributeFilterNameKey,
	CustomAttributesFilterTypes,
	FILTERS_DEFAULT_VALUES
} from '../../features/filter-bar/filter-bar';
import RootStore from '../root-store';

export type ViewNames = 'PlanningView' | 'DispatchView';
const localStorageName = 'ls.taskGridDispatchFilter';

class FilterStore {
	filters = FILTERS_DEFAULT_VALUES;
	activeView: ViewNames;

	constructor(viewName: ViewNames) {
		makeObservable(this, {
			filters: observable.ref,
			availableCustomAttributesFilterTypes: computed,
			getFiltersListNonPreset: computed,
			loadFilterListFromLocalStorage: action
		});

		this.activeView = viewName;
	}

	private setDefaultUserFilters() {
		this.filters = FILTERS_DEFAULT_VALUES;
	}

	private setFilterToLocalStorage(useDefaultFilters = false) {
		const { isImpersonatedMode } = getRoot<RootStore>().data.usersStore;

		// LocalStorage data has higher priority over dashboard_config
		// If store it when Presets enabled it also will affect branch with disabled Presets
		if (!isImpersonatedMode) {
			localStorage.setItem(
				`${localStorageName}.${this.activeView}`,
				JSON.stringify(useDefaultFilters ? FILTERS_DEFAULT_VALUES : this.getFiltersListNonPreset)
			);
		}
	}

	loadFilterListFromLocalStorage() {
		const { isImpersonatedMode } = getRoot<RootStore>().data.usersStore;

		if (!isImpersonatedMode) {
			const filtersList = JSON.parse(localStorage.getItem(`${localStorageName}.${this.activeView}`));
			if (filtersList) {
				this.filters = { ...FILTERS_DEFAULT_VALUES, ...filtersList };
			}
		}
	}

	protected resetAllFiltersNonPreset() {
		this.setDefaultUserFilters();
		this.setFilterToLocalStorage(true);
	}

	get getFiltersListNonPreset() {
		return toJS(this.filters);
	}

	protected updateFilterListNonPreset(updateSections) {
		this.filters = {
			...this.getFiltersListNonPreset,
			...updateSections,
			...{ availableCustomAttributesFilterTypes: this.availableCustomAttributesFilterTypes }
		};

		this.setFilterToLocalStorage();
	}

	protected replaceFilterListNonPreset(newList) {
		this.filters = newList;
		this.setFilterToLocalStorage();
	}

	localStorageHasDataNonPreset(): boolean {
		return !!localStorage.getItem(`${localStorageName}.${this.activeView}`);
	}

	// TODO: verify how it is used in the end
	get availableCustomAttributesFilterTypes(): CustomAttributesFilterTypes {
		return getRoot<RootStore>().data.customAttributesStore.activeCustomAttributes.reduce((acc, attr) => {
			acc[`${customAttributeFilterNameKey}${attr.id}`] = attr.data_type;

			return acc;
		}, {});
	}
}

export default FilterStore;
